// useInfiniteScroll.js
import { useState, useEffect, useRef, useCallback } from "react";

export const useInfiniteScroll = (fetchFunction, params, { size = 15, getKey = (x) => x.id } = {}) => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);

    const stateRef = useRef({ loading: false, hasMore: true });
    useEffect(() => {
        stateRef.current = { loading, hasMore };
    }, [loading, hasMore]);

    const loadItems = useCallback(async (targetPage) => {
        if (stateRef.current.loading) return;

        setLoading(true);
        try {
            const res = await fetchFunction({ ...params, page: targetPage, size });

            // ✅ 백엔드 응답: { data: [...], recordsTotal: number }
            const list = res?.data ?? [];
            const total = res?.recordsTotal ?? 0;

            setItems((prev) => {
                const prevKeys = new Set(prev.map(getKey));
                const incoming = list.filter((x) => !prevKeys.has(getKey(x)));
                return targetPage === 0 ? incoming : [...prev, ...incoming];
            });

            const loadedCount = (targetPage === 0 ? list.length : (items.length + list.length));
            // 더 단순하게: 이번 페이지가 size보다 작으면 끝
            setHasMore(list.length === size && (items.length + list.length) < total);
        } catch (e) {
            console.error(e);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [fetchFunction, params, size, getKey, items.length]);

    const search = useCallback(() => {
        setItems([]);
        setPage(0);
        setHasMore(true);
        loadItems(0);
    }, [loadItems]);

    useEffect(() => {
        if (page === 0) return;
        loadItems(page);
    }, [page, loadItems]);

    // sentinel 방식 (테이블 아래 div)
    const observer = useRef(null);
    const lastItemRef = useCallback((node) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting && stateRef.current.hasMore && !stateRef.current.loading) {
                setPage((p) => p + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, []);

    return { items, loading, hasMore, lastItemRef, search };
};
